/**
 * W-4 搜索(WScreen-4 search.md)。页内搜索栏 + 品类 chips + 状态互斥区。
 * page 分页 + 去重;搜索历史(≤10 去重置顶 + 清空确认);空结果推荐;五态。
 * URL 真源:?q=<关键词>&cat=<L1 id>。Header 搜索按钮进本页;窄屏 Header 搜索框隐藏,本页承载真实输入。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCategories, useSearch } from '@/hooks/browse';
import { l1Nodes } from '@/lib/categoryTree';
import { dedupeById } from '@/lib/dedupe';
import { clearSearchHistory, getSearchHistory, pushSearchHistory } from '@/lib/browseStore';
import { CategoryTabs } from '@/components/browse/CategoryTabs';
import { ProductCard } from '@/components/browse/ProductCard';
import { ProductGrid } from '@/components/browse/ProductGrid';
import { GridSkeleton } from '@/components/browse/GridSkeleton';
import { StateBlock } from '@/components/browse/StateBlock';
import { Breadcrumb } from '@/components/browse/Breadcrumb';
import { ConfirmDialog } from '@/components/browse/ConfirmDialog';

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const hasQuery = q.trim().length > 0; // 与 useSearch enabled 口径一致(纯空格 URL 也归历史区)
  const cat = params.get('cat');
  const [input, setInput] = useState(q);
  const [history, setHistory] = useState<string[]>(() => getSearchHistory());
  const [confirmClear, setConfirmClear] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInput(q);
  }, [q]);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const categoriesQ = useCategories();
  const tabs = useMemo(() => l1Nodes(categoriesQ.data ?? []), [categoriesQ.data]);
  const search = useSearch(q, cat ?? undefined);

  const items = useMemo(
    () => dedupeById((search.data?.pages ?? []).flatMap((p) => p.items ?? [])),
    [search.data],
  );
  const recommended = search.data?.pages?.[0]?.recommended ?? [];
  const total = search.data?.pages?.[0]?.total ?? 0;

  const submit = (term: string) => {
    const t = term.trim();
    if (!t) return; // 空白/纯空格提交:停留,不发请求
    setHistory(pushSearchHistory(t));
    const next: Record<string, string> = { q: t };
    if (cat) next.cat = cat;
    setParams(next);
  };

  const selectCat = (id: string | null) => {
    const next: Record<string, string> = {};
    if (q) next.q = q;
    if (id) next.cat = id;
    setParams(next); // q 空时仅切 chip:useSearch enabled=false 不发请求
  };

  return (
    <div className="inner section">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <form
        className="row gap-2 mb-6"
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
      >
        <input
          ref={inputRef}
          className="input"
          placeholder="搜技能、陪伴、达人…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          style={{ flex: 1 }}
          aria-label="搜索关键词"
        />
        <button type="submit" className="btn btn-primary">
          搜索
        </button>
      </form>

      {hasQuery ? (
        <h1 className="h2 mb-6">
          “{q}” 的搜索结果{' '}
          <span className="muted sm" style={{ fontWeight: 'var(--weight-medium)' }}>
            共 {total} 个商品
          </span>
        </h1>
      ) : null}

      <CategoryTabs categories={tabs} selectedId={cat} onSelect={selectCat} />

      {!hasQuery ? (
        history.length === 0 ? (
          <StateBlock icon="🔎" title="输入关键词开始搜索" desc="搜技能、陪伴、达人……" />
        ) : (
          <div>
            <div className="row between mb-4">
              <span className="sm muted">搜索历史</span>
              <button type="button" className="btn btn-ghost sm" onClick={() => setConfirmClear(true)}>
                清空
              </button>
            </div>
            <div className="row gap-2 wrap">
              {history.map((h) => (
                <button
                  key={h}
                  type="button"
                  className="tag"
                  style={{ border: 'none' }}
                  onClick={() => {
                    setInput(h);
                    submit(h);
                  }}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
        )
      ) : search.isLoading ? (
        <GridSkeleton />
      ) : search.isError && items.length === 0 ? (
        <StateBlock icon="⚠️" title="网络异常,稍后再试" desc="可重新提交或切换品类重试" />
      ) : items.length === 0 ? (
        <div>
          <StateBlock icon="📭" title="没找到相关商品,试试其他关键词" />
          {recommended.length > 0 ? (
            <>
              <div className="row between mb-6 mt-6">
                <h2 className="h2">为你推荐</h2>
              </div>
              <section className="grid-cards">
                {recommended.map((it, i) => (
                  <ProductCard key={it.productId ?? `rec-${i}`} item={it} />
                ))}
              </section>
            </>
          ) : null}
        </div>
      ) : (
        <ProductGrid
          items={items}
          hasNextPage={search.hasNextPage}
          isFetchingNextPage={search.isFetchingNextPage}
          isPageError={search.isError && items.length > 0}
          onLoadMore={() => search.fetchNextPage()}
        />
      )}

      {confirmClear ? (
        <ConfirmDialog
          title="清空搜索历史?"
          body="清空后不可恢复。"
          confirmLabel="清空"
          onConfirm={() => {
            setHistory(clearSearchHistory());
            setConfirmClear(false);
          }}
          onCancel={() => setConfirmClear(false)}
        />
      ) : null}
    </div>
  );
}
